<div class="main-container" [formGroup]="parentForm">
  <h2 class="section-heading">
    {{ 'form.sources.title' | translate }}
    <span class="asterisk">{{ ' *' }}</span>
  </h2>
  <div class="sources-buttons-hints">
    <mat-label class="field-hint">{{ 'form.sources.select_sources' | translate }}</mat-label>
    <app-notice
      size="sm"
      icon="info"
      [title]="'notice.hints.source_options' | translate"
    ></app-notice>
  </div>
  <div class="sources-container">
    <div class="sources-form-container" formGroupName="sources">
      <ng-container *ngFor="let source of sourceFormStructure">
        <ng-container
          *ngTemplateOutlet="sourcesTemplate; context: { $implicit: source }"
        ></ng-container>
      </ng-container>
    </div>

    <ng-template #sourcesTemplate let-source>
      <div
        [formGroup]="getControlAsGroup('sources.' + source.type)"
        [id]="'group-' + source.globalIndex + '_' + source.type + '-' + source.localIndex"
        [@sourceAnimation]="getSourceAnimationState(source)"
      >
        <div *ngIf="!sourceButtons[source.type].repeatable" class="source-group-container">
          <ng-container *ngTemplateOutlet="sourceHeading"></ng-container>
          <ng-container
            *ngFor="let field of findFieldInTree(fieldTree, source.type)?.children ?? []"
          >
            <ng-container
              *ngTemplateOutlet="
                fieldsTemplate;
                context: {
                  $implicit: field,
                  formGroup: getControlAsGroup('sources.' + source.type)
                }
              "
            ></ng-container>
          </ng-container>
        </div>

        <div *ngIf="sourceButtons[source.type].repeatable" class="source-group-container">
          <ng-container *ngTemplateOutlet="sourceHeading"></ng-container>
          <ng-container
            *ngFor="let object of findFieldInTree(fieldTree, source.type)?.children ?? []"
          >
            <ng-container *ngFor="let field of object.options?.fields">
              <ng-container
                *ngTemplateOutlet="
                  subfieldsTemplate;
                  context: {
                    $implicit: field,
                    formGroup: getControlAsArray('sources.' + object.group + '.' + object.name).at(
                      source.localIndex
                    ),
                    path: 'reports.sources.' + object.group + '.' + object.name + '.'
                  }
                "
              ></ng-container>
            </ng-container>
          </ng-container>
        </div>

        <ng-template #sourceHeading>
          <div class="source-group-heading">
            <mat-icon class="material-symbols-outlined">
              {{ sourceButtons[source.type].icon }}
            </mat-icon>
            <span class="source-group-title">
              {{ sourceButtons[source.type].heading | translate }}
              <span *ngIf="sourceButtons[source.type].repeatable && source.localIndex > 0">
                #{{ source.localIndex + 1 }}
              </span>
            </span>
            <button class="remove-btn" (click)="handleRemove($event, source.globalIndex)">
              <mat-icon class="material-symbols-outlined" color="warn">close</mat-icon>
              {{ 'form.remove' | translate }}
            </button>
          </div>
        </ng-template>
      </div>
    </ng-template>

    <ng-template #fieldsTemplate let-field let-formGroup="formGroup">
      <div class="form-field" [formGroup]="formGroup">
        <mat-label class="field-label">
          {{ getTranslatedLabel(field.label) | translate
          }}<span *ngIf="isRequired(field, parentForm)" class="asterisk">{{ ' *' }}</span>
        </mat-label>

        <!-- input -->
        <ng-container *ngIf="field.interface === 'input'">
          <mat-form-field appearance="fill">
            <input
              matInput
              type="text"
              [formControlName]="field.name"
              [required]="isRequired(field, parentForm)"
              [placeholder]="getPlaceholder(field, parentForm) | translate"
            />
          </mat-form-field>
        </ng-container>

        <!-- input-multiline -->
        <ng-container *ngIf="field.interface === 'input-multiline'">
          <mat-form-field appearance="fill">
            <textarea
              matInput
              [formControlName]="field.name"
              [required]="isRequired(field, parentForm)"
              [placeholder]="getPlaceholder(field, parentForm) | translate"
              cdkTextareaAutosize
              cdkAutosizeMinRows="2"
              cdkAutosizeMaxRows="5"
            ></textarea>
          </mat-form-field>
        </ng-container>

        <!-- select-dropdown -->
        <ng-container *ngIf="field.interface === 'select-dropdown'">
          <mat-form-field appearance="fill">
            <mat-select
              [name]="field.name"
              [formControlName]="field.name"
              [placeholder]="getPlaceholder(field, parentForm) | translate"
              [required]="isRequired(field, parentForm)"
            >
              <mat-option
                *ngFor="let option of field.options['choices']"
                [value]="option['value']"
                [id]="'select-' + field.name + '-' + option['value']"
              >
                {{ option['text'] | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </ng-container>

        <!-- select-multiple-checkbox -->
        <ng-container
          *ngIf="field.interface === 'select-multiple-checkbox' && !!field.options?.choices"
        >
          <app-chips-select
            [options]="field.options['choices']"
            [allowOther]="field.options['allowOther'] ?? false"
            [formControlName]="field.name"
            [multiple]="true"
            [required]="isRequired(field, parentForm)"
          ></app-chips-select>
        </ng-container>

        <mat-label *ngIf="field.note" class="field-hint">{{ field.note | translate }}</mat-label>
      </div>
    </ng-template>

    <ng-template #subfieldsTemplate let-field let-formGroup="formGroup" let-path="path">
      <div class="form-field" [formGroup]="formGroup">
        <mat-label class="field-label">
          {{ path + field.name + '.field_name' | translate
          }}<span *ngIf="field.meta.required" class="asterisk">{{ ' ' }}*</span>
        </mat-label>

        <!-- input -->
        <ng-container *ngIf="field.meta.interface === 'input'">
          <mat-form-field appearance="fill">
            <input
              matInput
              type="text"
              [formControlName]="field.name"
              [required]="field.meta.required"
              [placeholder]="field.meta.options.placeholder | translate"
            />
          </mat-form-field>
          <mat-error *ngIf="formGroup.get(field.name)?.hasError('invalidUrl')">
            {{ 'form.errors.invalid_url' | translate }}
          </mat-error>
          <mat-error *ngIf="formGroup.get(field.name)?.hasError('maxlength')">
            {{ 'form.errors.max_length_exceeded' | translate }}
          </mat-error>
        </ng-container>

        <!-- input-multiline -->
        <ng-container *ngIf="field.meta.interface === 'input-multiline'">
          <mat-form-field appearance="fill">
            <textarea
              matInput
              [formControlName]="field.name"
              [required]="field.meta.required"
              [placeholder]="field.meta.options.placeholder | translate"
              cdkTextareaAutosize
              cdkAutosizeMinRows="2"
              cdkAutosizeMaxRows="5"
            ></textarea>
          </mat-form-field>
        </ng-container>

        <!-- select-dropdown -->
        <ng-container *ngIf="field.meta.interface === 'select-dropdown'">
          <mat-form-field appearance="fill">
            <mat-select
              [name]="field.name"
              [formControlName]="field.name"
              [placeholder]="field.meta.options.placeholder | translate"
              [required]="field.meta.required"
            >
              <mat-option
                *ngFor="let option of field.meta.options['choices']"
                [value]="option['value']"
                [id]="'select-' + field.name + '-' + option['value']"
              >
                {{ option['text'] | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </ng-container>

        <!-- select-multiple-checkbox -->
        <ng-container
          *ngIf="
            field.meta.interface === 'select-multiple-checkbox' && !!field.meta.options?.choices
          "
        >
          <app-chips-select
            [options]="field.meta.options['choices']"
            [allowOther]="field.options['allowOther'] ?? false"
            [formControlName]="field.name"
            [multiple]="true"
            [required]="field.meta.required"
            [placeholder]="field.meta.options.placeholder | translate"
          ></app-chips-select>
        </ng-container>

        <mat-label *ngIf="field.meta.note" class="field-hint">{{
          field.meta.note | translate
        }}</mat-label>
      </div>
    </ng-template>

    <div class="sources-buttons-container">
      <mat-label class="field-hint" *ngIf="sourceFormStructure.length > 0" style="margin: 16px 0">{{
        'form.sources.add_another_source' | translate
      }}</mat-label>
      <mat-chip-list>
        <mat-chip
          *ngFor="let btn of getSourceButtonValues()"
          [disabled]="!canAdd(btn.value)"
          (click)="handleAdd(btn.value)"
          class="source-button"
        >
          <mat-icon class="material-symbols-outlined">{{ btn.icon }}</mat-icon>
          <span class="source-button-text">{{ btn.text | translate }}</span>
        </mat-chip>
      </mat-chip-list>
    </div>
  </div>
</div>
